import { useId, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  ActivityBreakdown,
  ActivityReportRow,
  BadgeReportRow,
  CenterReportRow,
  EquipmentReportRow,
  ReportsOverview,
} from 'shared';
import { TypeChip, UniversoChip } from '@/components/activities/ActivityUi';
import BarList from '@/components/charts/BarList';
import DataTable, { type Column } from '@/components/charts/DataTable';
import { activityTypeLabel, universoLabel } from '@/lib/reportCsv';
import { formatCount, formatHours, formatShare } from '@/lib/reportFormat';

interface SectionProps {
  title: string;
  subtitle?: string;
  /** When set, the section says this instead of showing its content. */
  empty?: string | false;
  children: ReactNode;
  className?: string;
}

export function Section({ title, subtitle, empty = false, children, className = '' }: SectionProps) {
  const headingId = useId();
  return (
    <section className={`card p-5 ${className}`} aria-labelledby={headingId}>
      <h2 id={headingId} className="text-sm font-semibold text-foreground">
        {title}
      </h2>
      {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      <div className="mt-4">
        {empty ? <p className="text-sm text-muted-foreground py-6 text-center">{empty}</p> : children}
      </div>
    </section>
  );
}

const people = (row: { attendance: number; visitors: number }) => row.attendance + row.visitors;

const breakdownColumns = (first: string, label: (key: string) => string): Column<ActivityBreakdown>[] => [
  { key: 'key', header: first, cell: (r) => label(r.key) },
  { key: 'activities', header: 'Atividades', numeric: true, cell: (r) => formatCount(r.activities) },
  { key: 'sessions', header: 'Sessões', numeric: true, cell: (r) => formatCount(r.sessions) },
  { key: 'attendance', header: 'Presenças', numeric: true, cell: (r) => formatCount(r.attendance) },
  { key: 'visitors', header: 'Visitantes', numeric: true, cell: (r) => formatCount(r.visitors) },
];

export function ActivitiesByType({ report }: { report: ReportsOverview }) {
  return (
    <Section
      title="Atividades por tipo"
      subtitle="Clubes, Circuitos e Dias Abertos com sessões no período"
      empty={report.byType.length === 0 && 'Nenhuma sessão de atividade no período escolhido.'}
    >
      <DataTable
        caption="Atividades por tipo"
        columns={breakdownColumns('Tipo', activityTypeLabel)}
        rows={report.byType}
        rowKey={(r) => r.key}
        footer={[
          'Total',
          formatCount(report.byType.reduce((sum, r) => sum + r.activities, 0)),
          formatCount(report.summary.sessions),
          formatCount(report.summary.attendance),
          formatCount(report.summary.visitors),
        ]}
      />
    </Section>
  );
}

export function ActivitiesByUniverso({ report }: { report: ReportsOverview }) {
  const total = report.summary.attendance + report.summary.visitors;
  return (
    <Section
      title="Pessoas por universo"
      subtitle="Presenças e visitantes nas atividades de cada universo"
      empty={report.byUniverso.length === 0 && 'Nenhuma sessão de atividade no período escolhido.'}
    >
      <BarList
        formatValue={formatCount}
        rows={report.byUniverso.map((u) => ({
          key: u.key || 'none',
          label: universoLabel(u.key),
          value: people(u),
          note: `${formatShare(people(u), total)} · ${formatCount(u.sessions)} ${u.sessions === 1 ? 'sessão' : 'sessões'}`,
        }))}
      />
    </Section>
  );
}

const activityColumns: Column<ActivityReportRow>[] = [
  {
    key: 'name',
    header: 'Atividade',
    cell: (a) => (
      <Link to={`/atividades/${a.id}`} className="font-medium text-foreground hover:text-primary hover:underline">
        {a.name}
      </Link>
    ),
  },
  { key: 'type', header: 'Tipo', cell: (a) => <TypeChip type={a.type} /> },
  {
    key: 'universo',
    header: 'Universo',
    cell: (a) => (a.universo ? <UniversoChip universo={a.universo} /> : <span className="text-muted-foreground">—</span>),
  },
  { key: 'centre', header: 'Centro', cell: (a) => a.digitalCenterName },
  { key: 'sessions', header: 'Sessões', numeric: true, cell: (a) => formatCount(a.sessions) },
  { key: 'attendance', header: 'Presenças', numeric: true, cell: (a) => formatCount(a.attendance) },
  { key: 'visitors', header: 'Visitantes', numeric: true, cell: (a) => formatCount(a.visitors) },
  {
    key: 'mean',
    header: 'Pessoas por sessão',
    numeric: true,
    cell: (a) => (a.sessions > 0 ? formatCount(Math.round(people(a) / a.sessions)) : '—'),
  },
];

export function ActivitiesTable({ report }: { report: ReportsOverview }) {
  return (
    <Section
      title="Atividades"
      subtitle="As que juntaram mais pessoas, primeiro"
      empty={report.activities.length === 0 && 'Nenhuma sessão de atividade no período escolhido.'}
    >
      <div className="overflow-x-auto">
        <DataTable caption="Atividades do período" columns={activityColumns} rows={report.activities} rowKey={(a) => a.id} />
      </div>
    </Section>
  );
}

const equipmentColumns: Column<EquipmentReportRow>[] = [
  { key: 'name', header: 'Equipamento', cell: (e) => <span className="font-medium">{e.name}</span> },
  { key: 'category', header: 'Categoria', cell: (e) => e.category },
  { key: 'centre', header: 'Centro', cell: (e) => e.digitalCenterName },
  { key: 'sessions', header: 'Utilizações', numeric: true, cell: (e) => formatCount(e.sessions) },
  { key: 'participants', header: 'Participantes', numeric: true, cell: (e) => formatCount(e.participants) },
  { key: 'hours', header: 'Horas', numeric: true, cell: (e) => formatHours(e.hours) },
];

export function EquipmentTable({ report }: { report: ReportsOverview }) {
  return (
    <Section
      title="Equipamento mais usado"
      subtitle="Por horas de utilização"
      empty={report.equipment.length === 0 && 'Nenhum equipamento foi usado no período escolhido.'}
    >
      <div className="overflow-x-auto">
        <DataTable caption="Equipamento mais usado" columns={equipmentColumns} rows={report.equipment} rowKey={(e) => e.id} />
      </div>
    </Section>
  );
}

const centreColumns: Column<CenterReportRow>[] = [
  { key: 'name', header: 'Centro', cell: (c) => <span className="font-medium">{c.name}</span> },
  { key: 'visits', header: 'Entradas', numeric: true, cell: (c) => formatCount(c.visits) },
  { key: 'attendance', header: 'Presenças em atividades', numeric: true, cell: (c) => formatCount(c.attendance) },
  { key: 'visitors', header: 'Visitantes em grupo', numeric: true, cell: (c) => formatCount(c.visitors) },
  { key: 'hours', header: 'Horas de equipamento', numeric: true, cell: (c) => formatHours(c.equipmentHours) },
];

export function CentresTable({ report }: { report: ReportsOverview }) {
  return (
    <Section title="Por centro" subtitle="Os centros onde houve movimento no período">
      <div className="overflow-x-auto">
        <DataTable caption="Movimento por centro" columns={centreColumns} rows={report.byCenter} rowKey={(c) => c.id} />
      </div>
    </Section>
  );
}

const badgeColumns: Column<BadgeReportRow>[] = [
  { key: 'name', header: 'Credencial', cell: (b) => <span className="font-medium">{b.name}</span> },
  {
    key: 'universo',
    header: 'Universo',
    cell: (b) => (b.universo ? <UniversoChip universo={b.universo} /> : <span className="text-muted-foreground">—</span>),
  },
  { key: 'awarded', header: 'Atribuídas', numeric: true, cell: (b) => formatCount(b.awarded) },
];

export function BadgesTable({ report }: { report: ReportsOverview }) {
  return (
    <Section
      title="Credenciais atribuídas"
      subtitle="As mais atribuídas no período"
      empty={report.badges.length === 0 && 'Nenhuma credencial atribuída no período escolhido.'}
    >
      <DataTable caption="Credenciais atribuídas" columns={badgeColumns} rows={report.badges} rowKey={(b) => b.id} />
    </Section>
  );
}

export function Demographics({ report }: { report: ReportsOverview }) {
  const { ageGroups, gender } = report;
  const share = (n: number, total: number) => formatShare(n, total);

  return (
    <Section
      title="Quem veio"
      subtitle={`${formatCount(ageGroups.total)} participantes diferentes com entrada ou presença no período`}
      empty={ageGroups.total === 0 && 'Nenhum participante veio no período escolhido.'}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <h3 className="text-xs font-medium text-muted-foreground mb-3">Idade</h3>
          <BarList
            formatValue={formatCount}
            max={ageGroups.total}
            rows={[
              { key: 'criancas', label: 'Até 14 anos', value: ageGroups.criancas, note: share(ageGroups.criancas, ageGroups.total) },
              { key: 'jovens', label: '15 a 24 anos', value: ageGroups.jovens, note: share(ageGroups.jovens, ageGroups.total) },
              { key: 'adultos', label: '25 a 64 anos', value: ageGroups.adultos, note: share(ageGroups.adultos, ageGroups.total) },
              { key: 'seniores', label: '65 anos ou mais', value: ageGroups.seniores, note: share(ageGroups.seniores, ageGroups.total) },
            ]}
          />
        </div>
        <div>
          <h3 className="text-xs font-medium text-muted-foreground mb-3">Género</h3>
          <BarList
            formatValue={formatCount}
            max={gender.total}
            rows={[
              { key: 'f', label: 'Feminino', value: gender.feminino, note: share(gender.feminino, gender.total) },
              { key: 'm', label: 'Masculino', value: gender.masculino, note: share(gender.masculino, gender.total) },
              { key: 'i', label: 'Indefinido', value: gender.indefinido, note: share(gender.indefinido, gender.total) },
            ]}
          />
        </div>
      </div>
    </Section>
  );
}
